import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../Global/Layout';
import DataTable from '../../Global/DataTable';
import ExportExcel from '../../Global/ExportToExcel';
import { showError } from '../../../utils/sweetAlertConfig';
import { API_CONFIG, ICON_ACTION, LOTTO_TYPE } from '../../../contexts/constants';

const ReportMembersAgent = ({ onLogout, userRole }) => {
  const location = useLocation();
  const periodState = location.state || {};

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [columnsExcelData, setColumnsExcelData] = useState([]);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    agentName: ''
  });
  const [searchFilters, setSearchFilters] = useState({
    agentName: ''
  });

  useEffect(() => {
    fetchDataList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchFilters]);

  	if (!['sysadmin', 'owner', 'admin'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

  const fetchDataList = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('pageSize', pageSize);

      // Add search filters
      if (searchFilters.agentName) {
        params.append('agentName', searchFilters.agentName);
      }

      // Add period filter from navigation state
      if (periodState.periodId) {
        params.append('periodId', periodState.periodId);
      }

      const response = await axios.get(`${API_CONFIG.BASE_URL}/report/members-agent?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success && Array.isArray(response.data.data)) {
        console.log("API Response Data:", response.data.data);
        setData(response.data.data);
        setTotalItems(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);

        const formattedData = response.data.data.map(r => ({
          agentName: (r.agentName ?? '').toString().replace(/"/g, ''),
          numMember: r.numMember ?? '',
        }));
        setColumnsExcelData(formattedData);
      } else {
        console.error('Invalid results data format');
        setData([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      showError('เกิดข้อผิดพลาด', 'ไม่สามารถดึงข้อมูลได้ในขณะนี้');
      setData([]);
    }finally {
      setLoading(false);
    }
  };

  const headersExcel = [
    { key: 'agentName', label: 'ชื่อตัวแทน' },
    { key: 'numMember', label: 'จำนวนลูกค้า' },
  ];

  const columns = [
    {
      key: 'agentName',
      header: 'ชื่อตัวแทน',
      render: (row) => (
        <div className="text-left">{row.agentName}</div>
      )
    },
    {
      key: 'numMember',
      header: 'จำนวนลูกค้า',
      render: (row) => (
        <div className="text-center">{row.numMember}</div>
      )
    },
  ];

  const safeTotalPages = Math.max(1, totalPages);
  const safeCurrentPage = Math.min(currentPage, safeTotalPages);

  const gotoPage = (p) => {
    if (p < 1 || p > safeTotalPages) return;
    setCurrentPage(p);
  };

  const handleSearch = () => {
    setSearchFilters({ ...filters });
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setFilters({ agentName: '' });
    setSearchFilters({ agentName: '' });
    setCurrentPage(1);
  };

  const toggleSearchForm = () => {
		setShowSearchForm(!showSearchForm);
	};

  return (
    <Layout title="รายงานจำนวนลูกค้าในเครือของตัวแทน" onLogout={onLogout} userRole={userRole}>

      {/* Period filter indicator */}
      {periodState.periodId && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 dark:text-gray-400">กรองตามงวด:</span>
          {periodState.lotType && (
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              periodState.lotType === 'powerball'
                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
            }`}>
              {LOTTO_TYPE[periodState.lotType]}
            </span>
          )}
          {periodState.periodLabel && (
            <span className="text-sm font-semibold text-gray-800 dark:text-white bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
              {periodState.periodLabel}
            </span>
          )}
          <span className="text-xs text-gray-500 dark:text-gray-400">(แสดงเฉพาะตัวแทนที่มีลูกค้าในงวดนี้)</span>
        </div>
      )}

      <div className="flex justify-end gap-3 mb-4">
        {/* Toggle Search */}
        <button
          onClick={toggleSearchForm}
          className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition shadow-lg ${
            showSearchForm
            ? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
            : 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
          }`}
        >
          {showSearchForm ? ICON_ACTION.CLOSE : ICON_ACTION.SEARCH}
          <span className='hidden md:inline'>ค้นหา</span>
        </button>
        {/* Export CSV */}
        <ExportExcel
          data={columnsExcelData}
          headers={headersExcel}
          fileName={`ReportWining${new Date().toISOString().slice(0,19).replace(/[:T]/g, '-')}.xlsx`}
        />
      </div>

      {showSearchForm && (
        <div className="mb-6 bg-white dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl shadow-card dark:shadow-carddark p-4 animate-fade-in">
          <div className="grid grid-cols-1 gap-4 mb-4">
            {/* ชื่อตัวแทน */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                ชื่อตัวแทน
              </label>
              <input
                type="text"
                value={filters.agentName}
                onChange={(e) => setFilters(prev => ({ ...prev, agentName: e.target.value }))}
                placeholder="ระบุชื่อตัวแทน"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
            >
              {ICON_ACTION.SEARCH}
              ค้นหา
            </button>
            <button
              onClick={handleClearFilter}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
            >
              {ICON_ACTION.CLEAR}
              ล้าง
            </button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        page={safeCurrentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={gotoPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
        emptyMessage="ไม่มีข้อมูล"
        showPagination={true}
      />
    </Layout>
  );

};

export default ReportMembersAgent;
